import type { PriorityColorMap, TagColorMap, Task, TaskSwipeActionT } from '@portfolio-pm/shared';
import { CalendarArrowUp, CircleCheck, EllipsisVertical, Repeat2, X } from 'lucide-react';
import {
  Fragment,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  type TouchEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDisplayPreferences } from '../../display/display-preferences.ts';
import { ScrollViewport } from '../../layout/ScrollViewport.tsx';
import {
  type TaskRowDateMode,
  formatTaskRowDateLabel,
  isTaskRowDateOverdue,
  startOfDayLocal,
} from '../task-view-dates.ts';
import type { TaskWorkspaceGroup } from '../task-workspace-model.ts';
import {
  POSTPONE_DELAY_UNITS,
  POSTPONE_OPTIONS,
  type PostponeDelayUnit,
  TASK_SWIPE_INTENT_PX,
  TASK_SWIPE_THRESHOLD_RATIO,
  type TaskDragState,
  type TaskDropPlacement,
} from './options.ts';
import {
  formatIntervalPreviewDate,
  formatMinutes,
  formatShortDate,
  getTagColor,
  matchesMobileTaskView,
  parsePositiveInteger,
  postponeTimestamp,
  triggerHapticClick,
} from './task-actions.ts';

/**
 * The grouped task list: scroll pane, group sections, swipeable and
 * draggable rows, plus the complete-children and mobile postpone
 * dialogs. Extracted from TaskWorkspace.tsx.
 */

export function TaskListPane({
  groups,
  listRef,
  activeTaskId,
  selectedTaskIds,
  priorityColors,
  tagColors,
  showBulkSelection,
  rowDateMode,
  swipeLeftToRight,
  swipeRightToLeft,
  manualOrdering,
  nestedSubtasks,
  onKeyDown,
  onSelectTask,
  onToggleTaskSelection,
  onTaskSwipeAction,
  onReorderTasks,
}: {
  groups: TaskWorkspaceGroup[];
  listRef: RefObject<HTMLDivElement>;
  activeTaskId: string | undefined;
  selectedTaskIds: Set<string>;
  priorityColors: PriorityColorMap;
  tagColors: TagColorMap;
  showBulkSelection: boolean;
  rowDateMode: TaskRowDateMode;
  swipeLeftToRight: TaskSwipeActionT;
  swipeRightToLeft: TaskSwipeActionT;
  manualOrdering: boolean;
  nestedSubtasks: boolean;
  onKeyDown(event: KeyboardEvent<HTMLDivElement>): void;
  onSelectTask(taskId: string): void;
  onToggleTaskSelection(taskId: string): void;
  onTaskSwipeAction(task: Task, action: TaskSwipeActionT): void;
  onReorderTasks(orderedTaskIds: string[]): void;
}): JSX.Element {
  const { preferences } = useDisplayPreferences();
  const [dragState, setDragState] = useState<TaskDragState | undefined>();
  const dragStateRef = useRef<TaskDragState | undefined>();
  const longPressTimerRef = useRef<number | undefined>();
  const orderedTasks = useMemo(() => groups.flatMap((group) => group.tasks), [groups]);
  const depthByTaskId = useMemo(
    () =>
      new Map(
        groups.flatMap((group) =>
          group.tasks.map((task) => [task.id, group.depthByTaskId?.get(task.id) ?? 0] as const),
        ),
      ),
    [groups],
  );

  const dragContextRef = useRef({ orderedTasks, selectedTaskIds, nestedSubtasks, depthByTaskId });
  dragContextRef.current = { orderedTasks, selectedTaskIds, nestedSubtasks, depthByTaskId };

  const beginDrag = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>, taskId: string): void => {
      if (event.button !== 0 && event.button !== undefined) return;
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      const startDrag = (): void => {
        const context = dragContextRef.current;
        const selectedMovingIds = context.orderedTasks
          .filter(
            (task) =>
              context.selectedTaskIds.has(task.id) &&
              (!context.nestedSubtasks || (context.depthByTaskId.get(task.id) ?? 0) === 0),
          )
          .map((task) => task.id);
        const movingTaskIds =
          context.selectedTaskIds.has(taskId) && selectedMovingIds.length > 0
            ? selectedMovingIds
            : [taskId];
        const nextDragState = { movingTaskIds };
        dragStateRef.current = nextDragState;
        setDragState(nextDragState);
      };

      if (event.pointerType === 'touch') {
        longPressTimerRef.current = window.setTimeout(startDrag, 260);
      } else {
        startDrag();
      }
    },
    [],
  );

  const finishDrop = useCallback((): void => {
    const currentDragState = dragStateRef.current;
    if (!currentDragState?.targetTaskId || !currentDragState.placement) {
      dragStateRef.current = undefined;
      setDragState(undefined);
      return;
    }
    onReorderTasks(
      reorderTaskIdsForDrop(
        orderedTasks.map((task) => task.id),
        currentDragState.movingTaskIds,
        currentDragState.targetTaskId,
        currentDragState.placement,
      ),
    );
    dragStateRef.current = undefined;
    setDragState(undefined);
  }, [onReorderTasks, orderedTasks]);

  useEffect(() => {
    if (!dragState) return;

    const handlePointerMove = (event: PointerEvent): void => {
      const currentDragState = dragStateRef.current;
      if (!currentDragState) return;
      const rowElements = [
        ...(listRef.current?.querySelectorAll<HTMLElement>('[data-task-id]') ?? []),
      ].filter((row) => {
        const taskId = row.dataset.taskId;
        return taskId !== undefined && !currentDragState.movingTaskIds.includes(taskId);
      });
      if (rowElements.length === 0) return;

      let targetRow = rowElements[rowElements.length - 1];
      let placement: TaskDropPlacement = 'after';
      for (const row of rowElements) {
        const bounds = row.getBoundingClientRect();
        if (event.clientY <= bounds.top + bounds.height / 2) {
          targetRow = row;
          placement = 'before';
          break;
        }
        if (event.clientY <= bounds.bottom) {
          targetRow = row;
          placement = 'after';
          break;
        }
      }

      const targetTaskId = targetRow?.dataset.taskId;
      const targetGroupId = targetRow?.dataset.groupId;
      if (!targetTaskId || !targetGroupId) return;
      if (
        currentDragState.targetTaskId === targetTaskId &&
        currentDragState.placement === placement
      ) {
        return;
      }
      const nextDragState: TaskDragState = {
        ...currentDragState,
        targetGroupId,
        targetTaskId,
        placement,
      };
      dragStateRef.current = nextDragState;
      setDragState(nextDragState);
    };

    const handlePointerEnd = (): void => finishDrop();
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd, { once: true });
    window.addEventListener('pointercancel', handlePointerEnd, { once: true });
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [dragState, finishDrop, listRef]);

  const cancelDrag = useCallback((): void => {
    if (longPressTimerRef.current !== undefined) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = undefined;
    }
    dragStateRef.current = undefined;
    setDragState(undefined);
  }, []);

  const cancelPendingDrag = useCallback((): void => {
    if (longPressTimerRef.current === undefined || dragStateRef.current) return;
    window.clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = undefined;
  }, []);

  return (
    <ScrollViewport
      viewportRef={listRef}
      data-testid="task-list"
      tabIndex={0}
      onKeyDown={onKeyDown}
      className={`mobile-task-list-frame min-h-0 flex-1 ${preferences.showMobileTaskEstimates ? '' : 'mobile-hide-task-estimates'}`}
      viewportClassName="task-list-scroll mobile-task-list-viewport h-full outline-none"
      contentClassName="max-md:pr-0"
    >
      {groups.length === 0 ? (
        <div className="p-4">
          <p className="surface-empty">
            No tasks match this view yet. Use quick add above to capture something new.
          </p>
        </div>
      ) : (
        groups.map((group) => (
          <TaskGroupSection
            key={group.id}
            group={group}
            activeTaskId={activeTaskId}
            selectedTaskIds={selectedTaskIds}
            priorityColors={priorityColors}
            tagColors={tagColors}
            showBulkSelection={showBulkSelection}
            rowDateMode={rowDateMode}
            swipeLeftToRight={swipeLeftToRight}
            swipeRightToLeft={swipeRightToLeft}
            manualOrdering={manualOrdering}
            nestedSubtasks={nestedSubtasks}
            dragState={dragState}
            onSelectTask={onSelectTask}
            onToggleTaskSelection={onToggleTaskSelection}
            onTaskSwipeAction={onTaskSwipeAction}
            onBeginDrag={beginDrag}
            onCancelDrag={cancelDrag}
            onCancelPendingDrag={cancelPendingDrag}
          />
        ))
      )}
    </ScrollViewport>
  );
}

export function CompleteChildrenDialog({
  taskCount,
  childrenCount,
  onSelectedOnly,
  onWithChildren,
  onCancel,
}: {
  taskCount: number;
  childrenCount: number;
  onSelectedOnly(): void;
  onWithChildren(): void;
  onCancel(): void;
}): JSX.Element {
  return (
    <div className="mobile-safe-overlay fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--app-modal-overlay)]">
      <div className="surface-panel max-w-md p-5">
        <p className="eyebrow-label">Complete Task</p>
        <h2 className="mt-2 text-lg font-semibold text-[color:var(--app-text)]">
          Complete child tasks too?
        </h2>
        <p className="mt-2 text-sm text-[color:var(--app-text-muted)]">
          {taskCount === 1 ? 'The selected task' : `${taskCount} selected tasks`} include{' '}
          {childrenCount} open child task{childrenCount === 1 ? '' : 's'}.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" onClick={onWithChildren} className="ui-button ui-button-primary">
            Complete all
          </button>
          <button type="button" onClick={onSelectedOnly} className="ui-button">
            Selected only
          </button>
          <button type="button" onClick={onCancel} className="ui-button ui-button-subtle">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function DeleteRecurringTaskDialog({
  taskCount,
  recurringCount,
  onInstances,
  onSeries,
  onCancel,
}: {
  taskCount: number;
  recurringCount: number;
  onInstances(): void;
  onSeries(): void;
  onCancel(): void;
}): JSX.Element {
  const subject =
    taskCount === 1
      ? 'this repeating task'
      : `${recurringCount} repeating task${recurringCount === 1 ? '' : 's'}`;

  return (
    <div className="mobile-safe-overlay fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--app-modal-overlay)]">
      <div className="surface-panel max-w-md p-5" data-testid="delete-recurring-task-dialog">
        <p className="eyebrow-label">Delete Repeating Task</p>
        <h2 className="mt-2 text-lg font-semibold text-[color:var(--app-text)]">
          Delete one instance or the series?
        </h2>
        <p className="mt-2 text-sm text-[color:var(--app-text-muted)]">
          Delete only {subject}, or move every matching task in the repeating series to Trash.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="delete-recurring-keep-series"
            onClick={onInstances}
            className="ui-button ui-button-primary"
          >
            Delete instance
          </button>
          <button
            type="button"
            data-testid="delete-recurring-delete-series"
            onClick={onSeries}
            className="ui-button"
          >
            Delete series
          </button>
          <button
            type="button"
            data-testid="delete-recurring-cancel"
            onClick={onCancel}
            className="ui-button ui-button-subtle"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function TaskGroupSection({
  group,
  activeTaskId,
  selectedTaskIds,
  priorityColors,
  tagColors,
  showBulkSelection,
  rowDateMode,
  swipeLeftToRight,
  swipeRightToLeft,
  manualOrdering,
  nestedSubtasks,
  dragState,
  onSelectTask,
  onToggleTaskSelection,
  onTaskSwipeAction,
  onBeginDrag,
  onCancelDrag,
  onCancelPendingDrag,
}: {
  group: TaskWorkspaceGroup;
  activeTaskId: string | undefined;
  selectedTaskIds: Set<string>;
  priorityColors: PriorityColorMap;
  tagColors: TagColorMap;
  showBulkSelection: boolean;
  rowDateMode: TaskRowDateMode;
  swipeLeftToRight: TaskSwipeActionT;
  swipeRightToLeft: TaskSwipeActionT;
  manualOrdering: boolean;
  nestedSubtasks: boolean;
  dragState: TaskDragState | undefined;
  onSelectTask(taskId: string): void;
  onToggleTaskSelection(taskId: string): void;
  onTaskSwipeAction(task: Task, action: TaskSwipeActionT): void;
  onBeginDrag(event: ReactPointerEvent<HTMLButtonElement>, taskId: string): void;
  onCancelDrag(): void;
  onCancelPendingDrag(): void;
}): JSX.Element {
  return (
    <section className="pane-divider-b">
      <div
        data-testid="task-group-header"
        className="pane-divider-b sticky top-0 z-10 bg-white px-3 py-1.5"
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="min-w-0 truncate text-sm font-medium text-[color:var(--app-text)]">
            {group.label}
          </h3>
          <span className="ui-chip px-2 py-0.5 text-[11px]">{group.tasks.length}</span>
        </div>
      </div>
      <ul>
        {group.tasks.map((task) => {
          const depth = group.depthByTaskId?.get(task.id) ?? 0;
          const dropPlacement =
            dragState?.targetGroupId === group.id && dragState.targetTaskId === task.id
              ? dragState.placement
              : undefined;
          const dropGap =
            dropPlacement && dragState ? (
              <TaskDropGap
                key={`${task.id}-${dropPlacement}`}
                movingCount={dragState.movingTaskIds.length}
              />
            ) : null;
          return (
            <Fragment key={task.id}>
              {dropPlacement === 'before' && dropGap}
              <TaskRow
                task={task}
                groupId={group.id}
                priorityColor={priorityColors[task.priority]}
                tagColors={tagColors}
                active={task.id === activeTaskId}
                bulkSelected={selectedTaskIds.has(task.id)}
                showBulkSelection={showBulkSelection}
                rowDateMode={rowDateMode}
                swipeLeftToRight={swipeLeftToRight}
                swipeRightToLeft={swipeRightToLeft}
                depth={depth}
                draggable={manualOrdering && (!nestedSubtasks || depth === 0)}
                dragging={dragState?.movingTaskIds.includes(task.id) ?? false}
                onSelect={onSelectTask}
                onToggleSelection={onToggleTaskSelection}
                onSwipeAction={onTaskSwipeAction}
                onBeginDrag={onBeginDrag}
                onCancelDrag={onCancelDrag}
                onCancelPendingDrag={onCancelPendingDrag}
              />
              {dropPlacement === 'after' && dropGap}
            </Fragment>
          );
        })}
      </ul>
    </section>
  );
}

function TaskDropGap({
  movingCount,
}: {
  movingCount: number;
}): JSX.Element {
  return (
    <li
      data-testid="task-drop-gap"
      aria-hidden="true"
      className="overflow-hidden bg-[color:var(--app-accent-soft)] transition-[height] duration-150"
      style={{ height: `${Math.max(1, movingCount) * 36}px` }}
    >
      <div className="h-full border-y border-dashed border-[color:var(--app-accent)] opacity-60" />
    </li>
  );
}

const TaskRow = memo(function TaskRow({
  task,
  groupId,
  priorityColor,
  tagColors,
  active,
  bulkSelected,
  showBulkSelection,
  rowDateMode,
  swipeLeftToRight,
  swipeRightToLeft,
  depth,
  draggable,
  dragging,
  onSelect,
  onToggleSelection,
  onSwipeAction,
  onBeginDrag,
  onCancelDrag,
  onCancelPendingDrag,
}: {
  task: Task;
  groupId: string;
  priorityColor: string;
  tagColors: TagColorMap;
  active: boolean;
  bulkSelected: boolean;
  showBulkSelection: boolean;
  rowDateMode: TaskRowDateMode;
  swipeLeftToRight: TaskSwipeActionT;
  swipeRightToLeft: TaskSwipeActionT;
  depth: number;
  draggable: boolean;
  dragging: boolean;
  onSelect(taskId: string): void;
  onToggleSelection(taskId: string): void;
  onSwipeAction(task: Task, action: TaskSwipeActionT): void;
  onBeginDrag(event: ReactPointerEvent<HTMLButtonElement>, taskId: string): void;
  onCancelDrag(): void;
  onCancelPendingDrag(): void;
}): JSX.Element {
  const touchStartRef = useRef<
    | {
        x: number;
        y: number;
        width: number;
        intent: 'pending' | 'horizontal' | 'vertical';
      }
    | undefined
  >();
  const swipeOffsetRef = useRef(0);
  const swipeArmedRef = useRef(false);
  const lastSwipeAtRef = useRef(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeArmed, setSwipeArmed] = useState(false);
  const [swipeSettling, setSwipeSettling] = useState(false);

  const resetSwipe = (settle: boolean): void => {
    touchStartRef.current = undefined;
    swipeOffsetRef.current = 0;
    swipeArmedRef.current = false;
    setSwipeSettling(settle);
    setSwipeOffset(0);
    setSwipeArmed(false);
  };

  const updateSwipe = (clientX: number, clientY: number): boolean => {
    const start = touchStartRef.current;
    if (!start) return false;

    const deltaX = clientX - start.x;
    const deltaY = clientY - start.y;
    if (start.intent === 'pending') {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < TASK_SWIPE_INTENT_PX) return false;
      start.intent = Math.abs(deltaX) > Math.abs(deltaY) * 1.2 ? 'horizontal' : 'vertical';
    }
    if (start.intent !== 'horizontal') return false;

    const nextOffset = Math.max(-start.width, Math.min(start.width, deltaX));
    const nextArmed = Math.abs(nextOffset) >= start.width * TASK_SWIPE_THRESHOLD_RATIO;
    swipeOffsetRef.current = nextOffset;
    setSwipeOffset(nextOffset);
    if (nextArmed !== swipeArmedRef.current) {
      swipeArmedRef.current = nextArmed;
      setSwipeArmed(nextArmed);
      triggerHapticClick();
    }
    return true;
  };

  const handleTouchStart = (event: TouchEvent<HTMLLIElement>): void => {
    if (!matchesMobileTaskView()) {
      touchStartRef.current = undefined;
      return;
    }
    const touch = event.touches[0];
    if (!touch) {
      touchStartRef.current = undefined;
      return;
    }
    const measuredWidth = event.currentTarget.getBoundingClientRect().width;
    setSwipeSettling(false);
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      width: measuredWidth > 0 ? measuredWidth : window.innerWidth,
      intent: 'pending',
    };
  };

  const handleTouchMove = (event: TouchEvent<HTMLLIElement>): void => {
    const touch = event.touches[0];
    if (!touch || !matchesMobileTaskView()) return;
    if (!updateSwipe(touch.clientX, touch.clientY)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const handleTouchEnd = (event: TouchEvent<HTMLLIElement>): void => {
    const start = touchStartRef.current;
    const end = event.changedTouches[0];
    if (!start || !end || !matchesMobileTaskView()) {
      resetSwipe(true);
      return;
    }

    const handled = updateSwipe(end.clientX, end.clientY);
    const finalOffset = swipeOffsetRef.current;
    const shouldCommit = handled && swipeArmedRef.current;
    if (start.intent === 'horizontal') lastSwipeAtRef.current = Date.now();
    resetSwipe(true);
    if (!handled) return;

    event.preventDefault();
    event.stopPropagation();
    if (shouldCommit) {
      onSwipeAction(task, finalOffset > 0 ? swipeLeftToRight : swipeRightToLeft);
    }
  };

  const handleTouchCancel = (): void => {
    if (touchStartRef.current?.intent === 'horizontal') lastSwipeAtRef.current = Date.now();
    resetSwipe(true);
  };

  const swipeAction =
    swipeOffset === 0 ? undefined : swipeOffset > 0 ? swipeLeftToRight : swipeRightToLeft;
  const swipeLabel = swipeAction === 'complete' ? 'Complete' : 'Postpone';
  const swipeProgress = Math.min(
    1,
    Math.abs(swipeOffset) /
      Math.max(1, (touchStartRef.current?.width ?? window.innerWidth) * TASK_SWIPE_THRESHOLD_RATIO),
  );

  return (
    <li
      data-testid="task-row"
      data-task-id={task.id}
      data-selected={bulkSelected || undefined}
      data-active={active || undefined}
      data-depth={depth}
      data-group-id={groupId}
      data-swipe-action={swipeAction}
      data-swipe-armed={swipeArmed || undefined}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      className={`group/task-row mobile-task-row pane-divider-b relative overflow-hidden last:border-b-0 transition-opacity ${
        dragging ? 'opacity-35' : ''
      }`}
    >
      {swipeAction && (
        <div
          data-testid="task-swipe-action"
          aria-hidden="true"
          className={`absolute inset-0 flex items-center px-5 text-sm font-semibold text-white ${
            swipeOffset > 0 ? 'justify-start' : 'justify-end'
          } ${
            swipeAction === 'complete'
              ? 'bg-[color:var(--app-info)]'
              : 'bg-[color:var(--app-warning)]'
          }`}
        >
          <span
            className={`flex items-center gap-2 transition-transform duration-100 ${
              swipeArmed ? 'scale-105' : ''
            }`}
            style={{ opacity: Math.max(0.45, swipeProgress) }}
          >
            {swipeAction === 'complete' ? (
              <CircleCheck aria-hidden="true" size={18} />
            ) : (
              <CalendarArrowUp aria-hidden="true" size={18} />
            )}
            {swipeLabel}
          </span>
        </div>
      )}
      <div
        data-testid="task-swipe-content"
        className={`mobile-task-row-content relative z-[1] flex w-full items-stretch gap-2 pr-3 text-left will-change-transform ${
          active
            ? 'bg-[color:var(--app-accent-soft)]'
            : 'bg-[color:var(--app-workspace)] hover:bg-[color:var(--app-surface-muted)]'
        }`}
        style={{
          paddingLeft: `${12 + depth * 20}px`,
          transform: `translate3d(${swipeOffset}px, 0, 0)`,
          transition: swipeSettling ? 'transform 180ms ease-out' : 'none',
        }}
      >
        {draggable ? (
          <button
            type="button"
            data-testid="task-drag-handle"
            aria-label={`Reorder ${task.title}`}
            title="Drag to reorder"
            onPointerDown={(event) => onBeginDrag(event, task.id)}
            onPointerMove={onCancelPendingDrag}
            onPointerUp={onCancelPendingDrag}
            onPointerCancel={onCancelDrag}
            className="-ml-2 flex w-7 shrink-0 self-stretch touch-none cursor-grab items-stretch justify-end gap-0.5 active:cursor-grabbing"
          >
            <EllipsisVertical
              aria-hidden="true"
              size={13}
              className="self-center opacity-70 transition-opacity md:opacity-0 md:group-hover/task-row:opacity-70"
            />
            <span
              data-testid="task-priority-marker"
              title={`P${task.priority || 'none'}`}
              className="task-priority-marker my-1 w-2 shrink-0 self-stretch rounded-sm"
              style={{ backgroundColor: priorityColor }}
              aria-hidden="true"
            />
          </button>
        ) : (
          <span
            data-testid="task-priority-marker"
            title={`P${task.priority || 'none'}`}
            className="task-priority-marker my-1 w-2 shrink-0 self-stretch rounded-sm"
            style={{ backgroundColor: priorityColor }}
            aria-hidden="true"
          />
        )}
        {showBulkSelection && (
          <button
            type="button"
            aria-label={bulkSelected ? 'Deselect task' : 'Select task'}
            onClick={(event) => {
              event.stopPropagation();
              onToggleSelection(task.id);
            }}
            className={`flex h-4 w-4 shrink-0 self-center items-center justify-center rounded-sm border text-[10px] ${
              bulkSelected
                ? 'border-[color:var(--app-info)] bg-[color:var(--app-info-soft)] text-[color:var(--app-info)]'
                : 'border-[color:var(--app-border-strong)] bg-white text-transparent'
            }`}
          >
            {bulkSelected ? '✓' : ''}
          </button>
        )}
        <button
          type="button"
          onClick={(event) => {
            if (Date.now() - lastSwipeAtRef.current < 500) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            onSelect(task.id);
          }}
          data-testid="task-row-content"
          className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_max-content] items-center gap-2 rounded-md py-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-[rgba(185,108,45,0.3)]"
        >
          <span data-testid="task-priority" className="sr-only">
            {task.priority === 0 ? 'None' : `P${task.priority}`}
          </span>
          <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 overflow-visible">
            <span
              data-testid="task-title-summary"
              className="flex min-w-0 max-w-full shrink basis-auto items-center gap-1"
            >
              <span
                data-testid="task-title"
                className={`truncate text-sm font-medium ${
                  task.completedAt !== undefined
                    ? 'text-[color:var(--app-text-soft)] line-through'
                    : 'text-[color:var(--app-text)]'
                }`}
              >
                {task.title}
              </span>
              {task.recurrence && (
                <Repeat2
                  data-testid="task-repeat-icon"
                  aria-label="Repeating task"
                  size={14}
                  className="shrink-0 text-[color:var(--app-text-soft)]"
                />
              )}
            </span>
            <div className="flex max-w-full flex-wrap items-center gap-1.5 overflow-visible">
              {task.tags.map((tag) => (
                <span
                  key={tag}
                  data-testid="task-tag"
                  className="ui-chip max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                  style={{
                    borderColor: `${getTagColor(tag, tagColors)}66`,
                    backgroundColor: `${getTagColor(tag, tagColors)}1f`,
                    color: getTagColor(tag, tagColors),
                  }}
                >
                  #{tag}
                </span>
              ))}
              {task.reminderAt !== undefined && (
                <span
                  data-testid="task-reminder-summary"
                  className="ui-chip ui-chip-warning max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  Remind {formatShortDate(task.reminderAt)}
                </span>
              )}
              {task.estimateMinutes !== undefined && (
                <span
                  data-testid="task-estimate-summary"
                  className="ui-chip max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  {formatMinutes(task.estimateMinutes)}
                </span>
              )}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <p
              data-testid="task-row-date"
              className={`truncate text-xs font-normal ${
                isTaskRowDateOverdue(task, rowDateMode)
                  ? 'text-[color:var(--app-danger)]'
                  : 'text-[color:var(--app-info)]'
              }`}
            >
              {formatTaskRowDateLabel(task, rowDateMode)}
            </p>
          </div>
        </button>
      </div>
    </li>
  );
});

export function MobilePostponeDelayDialog({
  taskTitle,
  onQuickSelect,
  onCustomSubmit,
  onCancel,
}: {
  taskTitle: string;
  onQuickSelect(delay: { amount: number; unit: PostponeDelayUnit }): void;
  onCustomSubmit(delay: { amount: number; unit: PostponeDelayUnit }): void;
  onCancel(): void;
}): JSX.Element {
  const [customOpen, setCustomOpen] = useState(false);
  const [amount, setAmount] = useState('1');
  const [unit, setUnit] = useState<PostponeDelayUnit>('days');
  const amountInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (customOpen) amountInputRef.current?.focus();
  }, [customOpen]);

  const previewAmount = parsePositiveInteger(amount) ?? 1;
  const previewTimestamp = postponeTimestamp(startOfDayLocal(Date.now()), {
    amount: previewAmount,
    unit,
  });
  const previewDate = formatIntervalPreviewDate(previewTimestamp);

  const submitCustom = (): void => {
    const parsedAmount = parsePositiveInteger(amount);
    if (parsedAmount === undefined) return;
    onCustomSubmit({ amount: parsedAmount, unit });
  };

  if (customOpen) {
    return (
      <div className="mobile-safe-overlay fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--app-modal-overlay)]">
        <form
          data-testid="mobile-postpone-custom-dialog"
          className="grid w-[min(26rem,94vw)] gap-4 rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-5 shadow-xl"
          onSubmit={(event) => {
            event.preventDefault();
            submitCustom();
          }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="eyebrow-label">Postpone</p>
              <h2 className="mt-1 text-lg font-semibold text-[color:var(--app-text)]">
                Custom delay
              </h2>
              <p className="mt-1 truncate text-sm text-[color:var(--app-text-muted)]">
                {taskTitle}
              </p>
            </div>
            <button
              type="button"
              aria-label="Close custom delay"
              onClick={onCancel}
              className="ui-button ui-button-subtle h-9 w-9 shrink-0 px-0"
            >
              <X aria-hidden="true" size={16} />
            </button>
          </div>

          <div
            data-testid="mobile-postpone-custom-preview"
            className="rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)] px-3 py-3 text-center text-sm font-medium text-[color:var(--app-text)]"
          >
            {previewDate}
          </div>

          <div className="grid gap-3">
            <label
              className="grid gap-1 text-sm font-medium text-[color:var(--app-text)]"
              htmlFor="mobile-postpone-custom-amount"
            >
              Number
              <input
                ref={amountInputRef}
                id="mobile-postpone-custom-amount"
                data-testid="mobile-postpone-custom-amount"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={amount}
                onChange={(event) => setAmount(event.target.value.replace(/\D/g, ''))}
                className="ui-input h-12 text-lg"
              />
            </label>
            <fieldset className="grid grid-cols-2 gap-2">
              <legend className="sr-only">Delay unit</legend>
              {POSTPONE_DELAY_UNITS.map((option) => {
                const selected = option.id === unit;
                return (
                  <button
                    key={option.id}
                    type="button"
                    data-testid={`mobile-postpone-custom-unit-${option.id}`}
                    aria-pressed={selected}
                    onClick={() => setUnit(option.id)}
                    className={`ui-button h-11 justify-center ${
                      selected ? 'ui-button-active' : 'ui-button-subtle'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </fieldset>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setCustomOpen(false)} className="ui-button h-11">
              Back
            </button>
            <button type="submit" className="ui-button ui-button-primary h-11">
              Done
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="mobile-safe-overlay fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--app-modal-overlay)]">
      <div
        data-testid="mobile-postpone-dialog"
        className="grid w-[min(24rem,94vw)] gap-4 rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-5 shadow-xl"
      >
        <div>
          <p className="eyebrow-label">Postpone</p>
          <h2 className="mt-1 text-lg font-semibold text-[color:var(--app-text)]">
            Choose a delay
          </h2>
          <p className="mt-1 truncate text-sm text-[color:var(--app-text-muted)]">{taskTitle}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {POSTPONE_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              data-testid={`mobile-postpone-option-${option.id}`}
              onClick={() => onQuickSelect(option.delay)}
              className="ui-button h-11 justify-start"
            >
              {option.label}
            </button>
          ))}
          <button
            type="button"
            data-testid="mobile-postpone-option-custom"
            onClick={() => setCustomOpen(true)}
            className="ui-button h-11 justify-start"
          >
            Custom
          </button>
        </div>
        <button type="button" onClick={onCancel} className="ui-button ui-button-subtle h-10">
          Cancel
        </button>
      </div>
    </div>
  );
}

export function reorderTaskIdsForDrop(
  orderedTaskIds: string[],
  movingTaskIds: string[],
  targetTaskId: string,
  placement: TaskDropPlacement,
): string[] {
  const movingSet = new Set(movingTaskIds);
  if (movingSet.has(targetTaskId)) return orderedTaskIds;
  const movingInOrder = orderedTaskIds.filter((id) => movingSet.has(id));
  const remaining = orderedTaskIds.filter((id) => !movingSet.has(id));
  const targetIndex = remaining.indexOf(targetTaskId);
  if (targetIndex === -1 || movingInOrder.length === 0) return orderedTaskIds;
  const insertionIndex = targetIndex + (placement === 'after' ? 1 : 0);
  return [
    ...remaining.slice(0, insertionIndex),
    ...movingInOrder,
    ...remaining.slice(insertionIndex),
  ];
}
